export {};

// External globals from types-fetch.js (loaded on same page)
declare function buildColorFilterFragment(): string;
declare function buildDesignFilterFragment(): string;

// Types for Storefront GraphQL product responses
interface PFImageNode {
  url: string;
  altText: string | null;
  id?: string;
}

interface PFImagesConnection {
  edges: { node: PFImageNode }[];
}

interface PFVariantBySelectedOptions {
  id: string;
  image: { url: string; altText: string | null } | null;
}

interface PFPriceRange {
  maxVariantPrice: { amount: string; currencyCode: string };
  minVariantPrice: { amount: string; currencyCode: string };
}

interface PFProduct {
  id: string;
  handle: string;
  title: string;
  availableForSale: boolean;
  vendor: string;
  productType: string;
  createdAt: string;
  tags: string[];
  metafield: { value: string } | null;
  featuredImage: PFImageNode | null;
  priceRange: PFPriceRange;
  variantBySelectedOptions?: PFVariantBySelectedOptions | null;
  variantYes?: PFVariantBySelectedOptions | null;
  variantNo?: PFVariantBySelectedOptions | null;
  images?: PFImagesConnection;
}

interface PFProductsConnection {
  pageInfo: { hasNextPage: boolean; endCursor: string };
  edges: { node: PFProduct }[];
}

interface PFGraphQLResponse {
  data?: {
    collection?: {
      products?: PFProductsConnection;
    };
  };
}

// Configuration
const pfState = {
  currentMarket: (document.querySelector('body[data-country]') as HTMLElement | null)?.dataset.country?.toUpperCase() === 'CA' ? 'CA' : 'US',
  collectionHandle: (document.querySelector('[data-collection-handle]') as HTMLElement | null)?.dataset.collectionHandle || '',
  currentLanguage: document.documentElement.lang.toUpperCase(),
  shopUrl: "https://kaseme-dev-page.myshopify.com",
  accessToken: "a815422836370e8d05bd0c6a813dbaa5"
};



// Utilitaires
class CookieManager {
  static setCookie(name: string, value: string, days = 14): void {
    const maxAge = days * 24 * 60 * 60;
    document.cookie = `${name}=${value}; path=/; max-age=${maxAge};`;
  }

  static getCookie(name: string): string | undefined {
    return document.cookie
      .split('; ')
      .find(row => row.startsWith(name + '='))
      ?.split('=')[1];
  }
}

// Détection du navigateur
function isSafari(version: number): boolean {
  const userAgent = navigator.userAgent;
  return userAgent.indexOf("Safari") !== -1 &&
         userAgent.indexOf("Chrome") === -1 &&
         parseFloat(userAgent.split("Version/")[1]) <= version;
}

// Fonctions de construction des requêtes GraphQL
function buildQuery(collectionHandle: string, first = 24, filters: string | null = null, queryAddon = ''): string {
  return `
    query FilteredProducts @inContext(country: ${pfState.currentMarket},language: ${pfState.currentLanguage}) {
      collection(handle: "${collectionHandle}") {
        products(first: ${first}, sortKey: COLLECTION_DEFAULT ${filters ? `, filters: ${filters}` : ''}) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              handle
              title
              availableForSale
              vendor
              productType
              createdAt
              tags
              ${queryAddon}
              metafield(namespace: "custom_fields", key: "related_products") {
                value
              }
              featuredImage {
                url
                altText
                id
              }
              priceRange {
                maxVariantPrice {
                  amount
                  currencyCode
                }
                minVariantPrice {
                  amount
                  currencyCode
                }
              }
            }
          }
        }
      }
    }
  `;
}

function buildMetafieldQuery(collectionHandle: string, metafield: string, first = 24): string {
  const filters = `[{productMetafield: {namespace: "custom", key: "subselector", value: "${metafield}"}}]`;
  const phoneDeviceValue = typeof CookieManager.getCookie('favPhone') === 'string' ? CookieManager.getCookie('favPhone'): 'iPhone 17';

  const queryAddon = `variantYes: variantBySelectedOptions(selectedOptions: [{ name:"Device", value:"${phoneDeviceValue}"},{ name:"Case Type", value:"Impact (Gloss)"},{ name:"MagSafe Ring", value:"Yes"}], ignoreUnknownOptions: true) {
      id
      image {
        url
        altText
      }
  }
  variantNo: variantBySelectedOptions(selectedOptions: [{ name:"Device", value:"${phoneDeviceValue}"},{ name:"Case Type", value:"Impact (Gloss)"},{ name:"MagSafe Ring", value:"No"}], ignoreUnknownOptions: true) {
      id
      image {
        url
        altText
      }
  }`;
  return buildQuery(collectionHandle, first, filters, queryAddon);
}

function buildProductTypeQuery(collectionHandle: string, productType: string, first = 24): string {
  switch (productType) {
    case 'Phone Case':
      return buildtcQuery(collectionHandle, first);
    case 'Clear Case':
      return buildccQuery(collectionHandle, first);
    case 'AirPod Cases':
      return buildacQuery(collectionHandle, first);
    case 'Folio Case':
      return buildfcQuery(collectionHandle, first);
    case 'iPad Folio Case':
      return buildicQuery(collectionHandle, first);
    case 'iPad Clear Case':
      return buildiccQuery(collectionHandle, first);
    case 'Skin':
      return buildmsQuery(collectionHandle, first);
    case 'ChargePods':
      return buildcpQuery(collectionHandle, first);
    case 'Kindle Case':
      return buildkcQuery(collectionHandle, first);
    default:
      return buildQuery(collectionHandle, first, '', '');
  }
}


function buildtcQuery(collectionHandle: string, first = 24): string {
  const filters = `[{ productType: "Phone Case"}]`;
  const deviceValue = CookieManager.getCookie('favPhone') || 'iPhone 17';
  const queryAddon = `variantYes: variantBySelectedOptions(selectedOptions: [{ name:"Device", value:"${deviceValue}"},{ name:"Case Type", value:"Impact (Gloss)"},{ name:"MagSafe Ring", value:"Yes"}], ignoreUnknownOptions: true) {
    id
    image {
      url
      altText
    }
  }
  variantNo: variantBySelectedOptions(selectedOptions: [{ name:"Device", value:"${deviceValue}"},{ name:"Case Type", value:"Impact (Gloss)"},{ name:"MagSafe Ring", value:"No"}], ignoreUnknownOptions: true) {
    id
    image {
      url
      altText
    }
  }`;
  return buildQuery(collectionHandle, first, filters, queryAddon);
}

function buildccQuery(collectionHandle: string, first = 12): string {
  let phoneDeviceValue = CookieManager.getCookie('favPhone');
  if (!phoneDeviceValue) {
    phoneDeviceValue = 'iPhone 17';
  }
  if(!phoneDeviceValue.includes('iPhone')) {
    phoneDeviceValue = 'iPhone 17';
  }
  const filters = `[{ productType: "Clear Case"},{variantOption: { name:"Device", value:"${phoneDeviceValue}"}}]`;

  let bumperType = 'Clear';
  const oldClearEl = document.querySelector('#old-clear') as HTMLInputElement | null;
  const oldClearList = oldClearEl?.value || '';
  if (oldClearList.includes(`|${phoneDeviceValue}|`)) {
    bumperType = 'White';
  }
  const queryAddon = `variantBySelectedOptions(selectedOptions: [{ name:"Device", value:"${phoneDeviceValue}"}, {name:"Bumper Color", value:"${bumperType}"}]) {
      id
      image {
        url
        altText
      }
    }`;
  return buildQuery(collectionHandle, first, filters, queryAddon);
}

function buildacQuery(collectionHandle: string, first = 12): string {
  const filters = `[{ productType: "Airpod Cases"}]`;
  const queryAddon = `images(first: 2) {
      edges {
        node {
          url
          altText
        }
      }
    }`;
  return buildQuery(collectionHandle, first, filters, queryAddon);
}

function buildmsQuery(collectionHandle: string, first = 12): string {
  const filters = '[{ productType: "Skin"}]';
  return buildQuery(collectionHandle, first, filters);
}

function buildcpQuery(collectionHandle: string, first = 12): string {
  const filters = '[{ productType: "ChargePods"}]';
  const queryAddon = `images(first: 2) {
      edges {
        node {
          url
          altText
        }
      }
    }`;
  return buildQuery(collectionHandle, first, filters, queryAddon);
}

function buildicQuery(collectionHandle: string, first = 12): string {
  const filters = '[{ productType: "iPad Folio Case"} ]';
  const queryAddon = `images (first: 3) {
    edges {
      node {
        id
        url
        altText
      }
    }
  }`;
  return buildQuery(collectionHandle, first, filters, queryAddon);
}

function buildiccQuery(collectionHandle: string, first = 12): string {
  const filters = '[{ productType: "iPad Clear Case"} ]';
  return buildQuery(collectionHandle, first, filters);
}

function buildfcQuery(collectionHandle: string, first = 12): string {
  const filters = '[{ productType: "Folio Case"},{productMetafield: {namespace: "custom", key: "folio_plain", value: "0"}}]';
  let phoneDeviceValue = CookieManager.getCookie('favPhone');
  if (!phoneDeviceValue) {
    phoneDeviceValue = 'iPhone 17';
  }
  if(!phoneDeviceValue.includes('Galaxy S2') && !phoneDeviceValue.includes('iPhone')) {
    phoneDeviceValue = 'iPhone 17';
  }
  const queryAddon = `variantBySelectedOptions(selectedOptions: [{ name:"Device", value:"${phoneDeviceValue}"}]) {
    id
    image {
      url
      altText
    }
  }`;
  return buildQuery(collectionHandle, first, filters, queryAddon);
}

// NOTE: buildkcQuery signature differs from other build*Query functions.
// When called from buildProductTypeQuery(collectionHandle, productType, first),
// the first arg (collectionHandle) maps to the `first` param name here,
// and second arg (first) maps to `cursor` — but values flow correctly to buildQuery.
// Preserving original param names to avoid changing behavior.
function buildkcQuery(first: string | number = 12, cursor: string | number | null = null): string {
  const colorFilter = buildColorFilterFragment();
  const designFilter = buildDesignFilterFragment();
  const filters = `[{ productType: "Kindle Case"}${colorFilter ? "," + colorFilter : ""}${designFilter ? "," + designFilter : ""}]`;
  const queryAddon = `images(first: 2) {
      edges {
        node {
          url
          altText
        }
      }
    }`;
  return buildQuery(first as string, cursor as number, filters, queryAddon);
}

const CARD_IMAGE_WIDTHS = [200, 250, 300, 350, 500, 600, 700];
const CARD_IMAGE_SIZES = '(min-width: 1200px) 300px, (min-width: 990px) 250px, (min-width: 750px) 350px, (min-width: 500px) 250px, 200px';

function buildResponsiveCardImg(url: string, alt: string | null | undefined): string {
  const srcset = CARD_IMAGE_WIDTHS.map((w) => `${url}&width=${w} ${w}w`).join(', ');
  return `<img src="${url}&width=300" srcset="${srcset}" sizes="${CARD_IMAGE_SIZES}" alt="${alt}" loading="lazy">`;
}

function  buildImage(currentProduct: PFProduct): string {
  let imageurl = currentProduct.featuredImage ? currentProduct.featuredImage.url : '';
  let imagealt = currentProduct.featuredImage ? currentProduct.featuredImage.altText : '';
  const selectedVariant = currentProduct.variantYes || currentProduct.variantNo || currentProduct.variantBySelectedOptions;
  if(selectedVariant) {
      imageurl = selectedVariant.image ? selectedVariant.image.url : '';
      imagealt = selectedVariant.image ? selectedVariant.image.altText : '';
  }
  let hoveraddon = '';
  if (currentProduct.productType == 'Folio Case' && currentProduct.featuredImage) {
    if (currentProduct.featuredImage) {
      const hoverURL = currentProduct.featuredImage.url;
      hoveraddon = `<div class="media media--transparent media--hover">
          ${buildResponsiveCardImg(hoverURL, hoverURL)}
        </div>`;
    }
  } else if (currentProduct.productType == 'iPad Folio Case') {
    const image = currentProduct.images?.edges[2]?.node;
    hoveraddon = '';
    if (image) {
      const hoverURL = image.url;
      const hoverAlt = image.altText;
      hoveraddon = `<div class="media media--transparent media--hover">
          ${buildResponsiveCardImg(hoverURL, hoverAlt ? hoverAlt : currentProduct.title)}
        </div>`;
    }

  } else if (currentProduct.productType == 'Airpod Cases') {
    const image = currentProduct.images?.edges[1]?.node;
    hoveraddon = '';
    if (image) {
      const hoverURL = image.url;
      const hoverAlt = image.altText;
      hoveraddon = `<div class="media media--transparent media--hover">
          ${buildResponsiveCardImg(hoverURL, hoverAlt ? hoverAlt : currentProduct.title)}
        </div>`;
    }
  } else if (currentProduct.productType == 'ChargePods') {
    const image = currentProduct.images?.edges[1]?.node;
    hoveraddon = '';
    if (image) {
      const hoverURL = image.url;
      const hoverAlt = image.altText;
      hoveraddon = `<div class="media media--transparent media--hover">
          ${buildResponsiveCardImg(hoverURL, hoverAlt ? hoverAlt : currentProduct.title)}
        </div>`;
    }
  }
  let imageSection = '';
  if (imageurl == '') {
    imageSection = `<div class="card__media">
        <div class="media media--transparent media--hover-effect --missing-image">
          <img src="${window.themeConfig?.assets?.loadingSpinner ?? ''}" alt="loading" width="75" height="75">
        </div>
    </div>`;
  } else {
    imageSection = `<div class="card__media">
      <div class="media media--transparent media--hover-effect">
        ${buildResponsiveCardImg(imageurl, imagealt)}
      </div>
      ${hoveraddon}
  </div>`;
  }


  return imageSection;
}

// Fonction pour construire le HTML d'un produit
function buildProductHtml(product: PFProduct, order: number): string {
  const { id, title, handle, availableForSale, variantBySelectedOptions, variantYes, variantNo, tags, vendor, productType, priceRange, metafield } = product;

  const isEarlyAccess = vendor === 'Early Access';

  const selectedVariant = variantYes || variantNo || variantBySelectedOptions;
  let variantId = selectedVariant ? selectedVariant.id.replace("gid://shopify/ProductVariant/", "") : null;
  let productUrl = handle;
  if(variantId) {
      productUrl = productUrl + '?variant=' + variantId;
  }

  let new_title = title;

  if (new_title.indexOf("Clear Case") >= 0) {
    new_title = new_title.substring(0, new_title.indexOf("Clear Case"));
  }
  if (new_title.indexOf("AirPods Case") >= 0) {
    new_title = new_title.substring(0, new_title.indexOf("AirPods Case"));
  }
  if (new_title.indexOf(" iPad Folio") >= 0) {
    new_title = new_title.substring(0, new_title.indexOf(" iPad Folio"));
  }
  if (new_title.indexOf("MacBook skin") >= 0) {
    new_title = new_title.substring(0, new_title.indexOf("MacBook skin"));
  }
  if (new_title.indexOf(" Folio Case") >= 0) {
    new_title = new_title.substring(0, new_title.indexOf(" Folio Case"));
  }

  let preprice = pfState.currentLanguage == 'FR' ? 'À partir de' : 'From';
  if (productType.indexOf('ChargePods') > -1) {
    preprice = '';
  } else if (priceRange.maxVariantPrice.amount == priceRange.minVariantPrice.amount) {
    preprice = '';
  } else if (productType.indexOf('iPad') > -1) {
    preprice = '';
  }

  const urlPrefix = pfState.currentLanguage == 'FR' ? '/fr' : '';

  let oosBadge = '';
  let newBadge = '';
  let colorLabel = '';
  const newTag = tags.filter(tag => {
    return tag == 'New';
  });

  if(!availableForSale) {
    oosBadge = pfState.currentLanguage == 'FR' ? '<span class="badge">Épuisé</span>':'<span class="badge">Out of Stock</span>';
  }

  if(newTag.length > 0) {
    newBadge = pfState.currentLanguage == 'FR' ? '<span class="badge">Nouveau</span>':'<span class="badge">New</span>';
  }

  const quickAddLabel = pfState.currentLanguage == 'FR' ? 'Ajout rapide':'Quick Add';
  if(metafield && metafield.value) {
    if (pfState.currentLanguage == 'FR') {
      colorLabel = `<div class="card__label">Plus de couleurs disponibles</div>`;
    } else {
      colorLabel = `<div class="card__label">More colors available</div>`;
    }
  }

  const productID = id.replace("gid://shopify/Product/", "");
  const quickAdd = `<div class="quick-add no-js-hidden">
      <modal-opener data-modal="#QuickAdd-${productID}">
        <button id="quick-add-template--${productID}-submit"
          type="submit" name="add"
          class="quick-add__submit button button--full-width button--outline" aria-haspopup="dialog"
          aria-labelledby="quick-add-template--${productID} title-template--${productID}"
          data-product-url="${urlPrefix}/products/${productUrl}">
          ${quickAddLabel}
          <div class="loading__spinner hidden">
            <img src="${window.themeConfig?.assets?.loadingSpinner ?? ''}" alt="loading" width="75" height="75">
          </div>
        </button>
      </modal-opener>
      <quick-add-modal id="QuickAdd-${productID}" class="quick-add-modal">
        <div
          role="dialog"
          aria-label="${new_title}"
          aria-modal="true"
          class="quick-add-modal__content global-settings-popup"
          tabindex="-1"
        >
          <button
            id="ModalClose-${productID}"
            type="button"
            class="quick-add-modal__toggle button button--outline"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" class="icon icon-close" fill="none" viewBox="0 0 18 17">
              <path d="M.865 15.978a.5.5 0 00.707.707l7.433-7.431 7.579 7.282a.501.501 0 00.846-.37.5.5 0 00-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 10-.707-.708L8.991 7.853 1.413.573a.5.5 0 10-.693.72l7.563 7.268-7.418 7.417z" fill="currentColor">
            </svg>
          </button>
          <div id="QuickAddInfo-${productID}" class="quick-add-modal__content-info"></div>
        </div>
      </quick-add-modal>
    </div>`;
  const clearCondition = productType.indexOf('Clear Case') > -1 && productType.indexOf('iPad') == -1;

  let price: number | string = clearCondition ? 34.99 : priceRange.minVariantPrice.amount;
  if(title.indexOf("DIY Queen Clear Case") > -1 || title.indexOf("DIY Queen transparent") > -1) {
    price = 39.95;
  }
  if (productType.indexOf('ChargePods') > -1) {
    price = 79.99;
    preprice = "";
  }
  const enPriceTag = pfState.currentLanguage == 'EN' ? '$' : '';
  const frPriceTag = pfState.currentLanguage == 'FR' ? '$' : '';
  const currencyTag = pfState.currentMarket === 'CA' ? ' CAD' : ' USD';
  const priceFormatted = parseFloat(String(price)).toFixed(2);

  let contentHTML = `<div class="price">
                  <div class="price__container">
                    <div class="price__regular">
                      ${preprice} ${enPriceTag}${priceFormatted}${frPriceTag}${currencyTag}
                    </div>
                  </div>
                </div>
                ${colorLabel}`;

  if(isEarlyAccess) {
    contentHTML = `<span class="card__badge badge-early-access">${pfState.currentLanguage == 'EN' ? 'Coming Soon' : 'Bientôt disponible'}</span>`;
  }
  return `
    <li class="grid__item" style="order: ${order}" data-order="${order}">
      <div class="card-wrapper product-card-wrapper underline-links-hover ${productType.indexOf('Airpod') > -1 ? '--airpods' : ''} ${productType.indexOf('iPad') > -1  ? '--ipad' : ''}">
        <a href="${urlPrefix}/products/${productUrl}">
          <span class="visually-hidden">${new_title}</span>
        </a>
        <div class="card card--standard card--media">
          <div class="card__inner">
            ${buildImage(product)}
          </div>
          <div class="card__content">
            <div class="card__information">
              <h3 class="card__heading">
                <a href="${productUrl}" class="full-unstyled-link">
                  ${new_title}
                </a>
              </h3>
              <div class="card-information">
                ${contentHTML}
              </div>
            </div>
            <!--${quickAdd}-->
          </div>
          <div class="card__badge top left">
            ${newBadge}
            ${oosBadge}
          </div>
        </div>
      </div>
    </li>
  `;
}


// Fonction de fetch principale
async function fetchCollectionPrefilteredProducts(Manager: ProductTypeManager): Promise<void | false> {
  const currentBuild = Manager.sectionType;
  try {

    const section = document.querySelector(`[data-prefiltered-section="${currentBuild}"]`) as HTMLElement | null;
    if (!section) {
      console.warn(`Section ${currentBuild} not found`);
      return;
    }
    const ulEl = section.querySelector('ul') as HTMLElement | null;
    const nb_products = ulEl?.dataset.ogn ? parseInt(ulEl.dataset.ogn) : 24;
    const metafield = currentBuild;
    const collectionHandle = Manager.collectionHandle;
    const ignoreMetafield = Manager.section.dataset.ignoreMetafield == "true";

    const query = ignoreMetafield ? buildProductTypeQuery(collectionHandle, metafield, nb_products) : buildMetafieldQuery(collectionHandle, metafield, nb_products);

    const response = await fetch(pfState.shopUrl + '/api/2025-07/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': pfState.accessToken
      },
      body: JSON.stringify({
        query: query
      })
    });

    if (!response.ok) throw new Error('Network response was not ok');
    const data: PFGraphQLResponse = await response.json();
    const products = data.data?.collection?.products;
    if(products) {
      if(products.edges.length > 0) {
        const currentList = section.querySelector('ul');
        if (currentList) {
          currentList.innerHTML = '';
          let productsListHTML = '';
          let lastOrder = 0;
          products.edges.forEach(({ node: product }, i) => {
            const order = i + 1;
            lastOrder = order;
            const promoToPlace = section.querySelectorAll(`template[data-position="${order}"]`);
            if(promoToPlace.length > 0) {
              promoToPlace.forEach((promo) => {
                productsListHTML += promo.innerHTML;
              });
            }
            productsListHTML += buildProductHtml(product, order);
          });
          const promoToPlace = section.querySelectorAll(`template[data-position="${lastOrder + 1}"]`);
          if(promoToPlace.length > 0) {
            promoToPlace.forEach((promo) => {
              productsListHTML += promo.innerHTML;
            });
          }
          currentList.innerHTML = productsListHTML;
        }

      } else {
        const currentList = section.querySelector('ul');
        if (currentList) {
          if (document.documentElement.lang.toUpperCase() == 'FR') {
            currentList.innerHTML = "<li class='empty'>Aucun produit avec le type d'appareil sélectionné</li>";
          } else {
            currentList.innerHTML = "<li class='empty'>No product with the selected device type</li>";
          }
        }
      }

      section.removeAttribute('hidden');
      section.parentElement?.removeAttribute('hidden');
    } else {
      // FIX: original called section.remove() then section.parentElement.setAttribute(...)
      // parentElement is null after remove(). Saving ref first.
      const parent = section.parentElement;
      section.remove();
      Manager.isValid = false;
      parent?.setAttribute('hidden', 'hidden');
    }
    return;
  } catch (error) {
    console.error('Error fetching products:', error);
    return false;
  }
}

// Gestionnaire de produits
class ProductTypeManager {
  section: HTMLElement;
  sectionType: string;
  isValid!: boolean;
  collectionHandle!: string;

  constructor(section: HTMLElement) {
    this.section = section;
    this.sectionType = section.dataset.prefilteredSection || '';
    if (!this.section) return;
    this.isValid = true;
    this.collectionHandle = (document.querySelector(`[data-prefiltered-section="${this.sectionType}"]`) as HTMLElement | null)?.dataset.collectionHandle || '';
    fetchCollectionPrefilteredProducts(
      this
    ).then(() => {
      const middlePromo = this.section.querySelector('[data-promo-container="middle"]') as HTMLElement | null;
      if(middlePromo) {
        const clone = middlePromo.querySelector('.promo')?.cloneNode(true);
        const position = middlePromo.dataset.promoPosition || '1';
        const targetEl = this.section.querySelector(`li:nth-of-type(${position})`);
        if (clone && targetEl?.parentElement) {
          targetEl.parentElement.insertBefore(clone, targetEl);
        }
        // NOTE: .hide() is not a standard DOM method — may be a custom element method
        if ('hide' in middlePromo && typeof (middlePromo as Record<string, unknown>).hide === 'function') {
          (middlePromo as unknown as { hide(): void }).hide();
        }
      }
    });
  }

  updateDevice(): void {
    fetchCollectionPrefilteredProducts(
      this
    ).then(() => {
      const middlePromo = this.section.querySelector('[data-promo-container="middle"]') as HTMLElement | null;
      if(middlePromo) {
        const clone = middlePromo.querySelector('.promo')?.cloneNode(true);
        const position = middlePromo.dataset.promoPosition || '1';
        const targetEl = this.section.querySelector(`li:nth-of-type(${position})`);
        if (clone && targetEl?.parentElement) {
          targetEl.parentElement.insertBefore(clone, targetEl);
        }
        if ('hide' in middlePromo && typeof (middlePromo as Record<string, unknown>).hide === 'function') {
          (middlePromo as unknown as { hide(): void }).hide();
        }
      }
    });
  }

}


// Gestionnaire de filtres
class FilterManager {
  AllManager: ProductTypeManager[];

  constructor(allManager: ProductTypeManager[]) {
    this.AllManager = allManager;
    this.initializePhoneSelector();
  }


  initializePhoneSelector(): void {
    const phoneSelector = document.querySelector('#phone-device') as HTMLSelectElement | null;
    if (!phoneSelector) return;
    if(phoneSelector.options.length > 0) {
      const currentFavPhone = CookieManager.getCookie('favPhone') || 'iPhone 17';
      phoneSelector.value = currentFavPhone;

      new Choices(phoneSelector, {shouldSort: false, shouldSortItems: false});
      this.setupSelectorEvents(phoneSelector);
    } else {
      // Walk up 5 parent levels as in original
      let el: HTMLElement | null = phoneSelector;
      for (let i = 0; i < 5 && el?.parentElement; i++) {
        el = el.parentElement;
      }
      el?.remove();
      document.querySelector('[data-toggle-device]')?.remove();
    }
  }

  setupSelectorEvents(selector: HTMLSelectElement): void {
    selector.addEventListener('change', () => {
      CookieManager.setCookie('favPhone', selector.value);
      this.AllManager.forEach(manager => manager.updateDevice());
    });
  }

}

// Initialisation
document.addEventListener('DOMContentLoaded', () => {
  if (isSafari(15.6)) {
    document.querySelectorAll('#MainContent')?.forEach(content => content.classList.add('--safari'));
  }

  const ProductTypeManagerVar: ProductTypeManager[] = [];
  document.querySelectorAll('[data-prefiltered-section]').forEach(section => {
    if ((section as HTMLElement).dataset.prefilteredSection) {
      ProductTypeManagerVar.push(new ProductTypeManager(section as HTMLElement));
    }
  });

  new FilterManager(ProductTypeManagerVar);
});
